// === INPACT:SLUITSTRAAT:SAA:MAIN:v2 ===

import React from 'react';
import ReactDOM from 'react-dom/client';
// Import defaultLanguage and i18n instance to apply SAA-specific translation overrides
import { defaultLanguage, bffClient, getCurrentBrand, authService } from '@sluitstraat/core';
import i18n from 'i18next';
import type { BrandId } from '@sluitstraat/core';
import App from './App';
import './index.css';
import 'flag-icons/css/flag-icons.min.css';

// SAA-specific translation overrides (loaded from local files)
import saaOverridesNl from './i18n/nl.json';
import saaOverridesEn from './i18n/en.json';
import saaInformalNl from './i18n/nl.nu91.json';
import { isInformalBrand } from './i18n/tone';
import { warnMissingProductContent } from './products/content';

// Log to verify i18n initialization (development only)
if (import.meta.env.DEV) {
  console.log('i18n default language:', defaultLanguage);
}

// Brand detection is synchronous (reads window.location), safe to run before React mounts
const detectedBrand = getCurrentBrand();

// Deep merge SAA-specific translations into the existing i18n resources
// This overrides shared core translations with SAA-local values
i18n.addResourceBundle('nl', 'translation', saaOverridesNl, true, true);
i18n.addResourceBundle('en', 'translation', saaOverridesEn, true, true);
if (isInformalBrand(detectedBrand)) {
  i18n.addResourceBundle('nl', 'translation', saaInformalNl, true, true);
}

warnMissingProductContent();

// Clear monument property flags on app initialization (fresh page load)
// These will be re-determined by address lookup during the session
try {
  sessionStorage.removeItem('woonhuis_monument_property');
  sessionStorage.removeItem('inboedel_monument_property');
} catch (e) {
  // Ignore sessionStorage errors
}

// Brand-to-tenant resolution for multi-brand SAA deployment (nu91 / dfd / spd)
const BRAND_TENANT_MAP: Record<BrandId, { tenantId: string; apiKey: string }> = {
  nu91: { tenantId: 'nu91', apiKey: import.meta.env.VITE_TENANT_KEY_NU91 ?? '' },
  dfd:  { tenantId: 'dfd',  apiKey: import.meta.env.VITE_TENANT_KEY_DFD ?? '' },
  spd:  { tenantId: 'spd',  apiKey: import.meta.env.VITE_TENANT_KEY_SPD ?? '' },
  default: { tenantId: 'nu91', apiKey: import.meta.env.VITE_TENANT_KEY_NU91 ?? '' },
};

const tenantConfig = BRAND_TENANT_MAP[detectedBrand];

bffClient.setTenantId(tenantConfig.tenantId);
authService.setApiKey(tenantConfig.apiKey);

if (import.meta.env.DEV) {
  console.log(`[SAA] Brand resolved: ${detectedBrand} -> tenant: ${tenantConfig.tenantId}`);
}

// Configure BFF client base URL from environment
if (import.meta.env.VITE_BFF_BASE_URL) {
  bffClient.configure(import.meta.env.VITE_BFF_BASE_URL);
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
